import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { PackingListService } from '../../services/packingList.service';
import { PERMISSIONS } from '../../constants/permissions';
import { CreatePackingListCartonRequest, PackingListCartonDTO } from '../../interfaces/commercial.interface';

const EMPTY = {
    cartonNo: '', description: '', colorName: '', sizeName: '', quantity: '',
    netWeight: '', grossWeight: '', dimensions: '',
};

/** Carton child-lines panel for a packing list header (15.2) — mirrors LcChecklistPanel's add/
 * edit/delete-row shape. Header roll-ups (totalCartons/totalQuantity/totalNetWeight/
 * totalGrossWeight) are recomputed server-side on every carton write; `onCartonsChanged` lets the
 * parent detail screen re-fetch the header to pick those up. */
const PackingListCartonPanel: React.FC<{ packingListId: string; onCartonsChanged?: () => void }> = ({
    packingListId, onCartonsChanged,
}) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<PackingListCartonDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            setRows(await PackingListService.listCartons(institutionId, packingListId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load carton lines');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [packingListId]); // eslint-disable-line react-hooks/exhaustive-deps

    const openAdd = () => {
        setEditingId(null);
        setForm({ ...EMPTY });
        setOpen(true);
    };

    const openEdit = (row: PackingListCartonDTO) => {
        setEditingId(row.id);
        setForm({
            cartonNo: row.cartonNo, description: row.description ?? '', colorName: row.colorName ?? '',
            sizeName: row.sizeName ?? '', quantity: row.quantity != null ? String(row.quantity) : '',
            netWeight: row.netWeight != null ? String(row.netWeight) : '',
            grossWeight: row.grossWeight != null ? String(row.grossWeight) : '',
            dimensions: row.dimensions ?? '',
        });
        setOpen(true);
    };

    const saveWhy = saving ? null
        : !form.cartonNo.trim() ? 'Enter the carton number.'
        : null;

    const handleSave = async () => {
        if (!form.cartonNo.trim()) return;
        const dto: CreatePackingListCartonRequest = {
            cartonNo: form.cartonNo.trim(),
            description: form.description.trim() || undefined,
            colorName: form.colorName.trim() || undefined,
            sizeName: form.sizeName.trim() || undefined,
            quantity: form.quantity.trim() ? Number(form.quantity) : undefined,
            netWeight: form.netWeight.trim() ? Number(form.netWeight) : undefined,
            grossWeight: form.grossWeight.trim() ? Number(form.grossWeight) : undefined,
            dimensions: form.dimensions.trim() || undefined,
        };
        try {
            setSaving(true);
            if (editingId) {
                await PackingListService.updateCarton(institutionId, packingListId, editingId, dto);
            } else {
                await PackingListService.createCarton(institutionId, packingListId, dto);
            }
            toast.showSuccess(editingId ? 'Carton updated' : 'Carton added');
            setOpen(false);
            setForm({ ...EMPTY });
            setEditingId(null);
            load();
            onCartonsChanged?.();
        } catch (e) {
            toast.showApiError(e, editingId ? 'Failed to update carton' : 'Failed to add carton');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (row: PackingListCartonDTO) => {
        try {
            await PackingListService.removeCarton(institutionId, packingListId, row.id);
            toast.showSuccess('Carton removed');
            load();
            onCartonsChanged?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to remove carton');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Carton Lines</Typography>
                {canManage && (
                    <Button data-help="commercial.cartons.add" startIcon={<AddIcon />} variant="outlined" onClick={openAdd}>
                        Add Carton
                    </Button>
                )}
            </Box>
            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.cartons.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.cartons.col.number">Carton No</TableCell>
                                <TableCell data-help="commercial.cartons.col.description">Description</TableCell>
                                <TableCell data-help="commercial.cartons.col.color">Color</TableCell>
                                <TableCell data-help="commercial.cartons.col.size">Size</TableCell>
                                <TableCell align="right" data-help="commercial.cartons.col.qty">Quantity</TableCell>
                                <TableCell align="right" data-help="commercial.cartons.col.net">Net Weight (kg)</TableCell>
                                <TableCell align="right" data-help="commercial.cartons.col.gross">Gross Weight (kg)</TableCell>
                                <TableCell data-help="commercial.cartons.col.dimensions">Dimensions</TableCell>
                                {canManage && <TableCell align="right" data-help="commercial.cartons.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.cartonNo}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{row.description ?? '—'}</TableCell>
                                    <TableCell>{row.colorName ?? '—'}</TableCell>
                                    <TableCell>{row.sizeName ?? '—'}</TableCell>
                                    <TableCell align="right">{row.quantity ?? '—'}</TableCell>
                                    <TableCell align="right">{row.netWeight ?? '—'}</TableCell>
                                    <TableCell align="right">{row.grossWeight ?? '—'}</TableCell>
                                    <TableCell>{row.dimensions ?? '—'}</TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <IconButton data-help="commercial.cartons.edit" size="small" onClick={() => openEdit(row)} aria-label={`edit-${row.cartonNo}`}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton data-help="commercial.cartons.remove" size="small" onClick={() => handleRemove(row)} aria-label={`remove-${row.cartonNo}`}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 9 : 8} align="center">No carton lines yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.cartons.dialog.page' } as any}>
                <DialogTitle>{editingId ? 'Edit Carton' : 'Add Carton'}</DialogTitle>
                <DialogContent>
                    <TextField
                        data-help="commercial.cartons.noField" label="Carton No" fullWidth required sx={{ mt: 1, mb: 2 }}
                        value={form.cartonNo}
                        onChange={(e) => setForm(f => ({ ...f, cartonNo: e.target.value }))}
                    />
                    <TextField
                        data-help="commercial.cartons.descField" label="Description" fullWidth sx={{ mb: 2 }}
                        value={form.description}
                        onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            data-help="commercial.cartons.colorField" label="Color" fullWidth value={form.colorName}
                            onChange={(e) => setForm(f => ({ ...f, colorName: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.cartons.sizeField" label="Size" fullWidth value={form.sizeName}
                            onChange={(e) => setForm(f => ({ ...f, sizeName: e.target.value }))}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            data-help="commercial.cartons.qtyField" label="Quantity" type="number" fullWidth value={form.quantity}
                            onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.cartons.netField" label="Net Weight (kg)" type="number" fullWidth value={form.netWeight}
                            onChange={(e) => setForm(f => ({ ...f, netWeight: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.cartons.grossField" label="Gross Weight (kg)" type="number" fullWidth value={form.grossWeight}
                            onChange={(e) => setForm(f => ({ ...f, grossWeight: e.target.value }))}
                        />
                    </Box>
                    <TextField
                        data-help="commercial.cartons.dimField" label="Dimensions (LxWxH cm)" fullWidth
                        value={form.dimensions}
                        onChange={(e) => setForm(f => ({ ...f, dimensions: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.cartons.cancel" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.cartons.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !form.cartonNo.trim()} onClick={handleSave}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PackingListCartonPanel;
